<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card elevation="4" :loading="loading">
          <v-card-title class="text-indigo text-button">Certificado</v-card-title>

          <v-container fluid>
            <v-row>
              <v-col cols="12">
                <h4 class="text-button text-primary">Identificación</h4>
                <v-divider />
                <v-container fluid>
                  <v-row>
                    <v-col cols="12" lg="8">
                      <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                      <v-text-field
                        v-else
                        :model-value="data.fingerprint"
                        label="Fingerprint"
                        prepend-inner-icon="mdi-fingerprint"
                        readonly
                      />
                    </v-col>
                    <v-col cols="12" sm="6" lg="4">
                      <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                      <v-text-field
                        v-else
                        :model-value="data.serialnumber"
                        label="Número de serie"
                        prepend-inner-icon="mdi-numeric"
                        readonly
                      />
                    </v-col>
                    <v-col cols="12" sm="6" lg="4">
                      <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                      <v-text-field
                        v-else
                        :model-value="formatDate(data.valid_after)"
                        label="Válido desde"
                        prepend-inner-icon="mdi-calendar-start"
                        readonly
                      />
                    </v-col>
                    <v-col cols="12" sm="6" lg="4">
                      <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                      <v-text-field
                        v-else
                        :model-value="formatDate(data.valid_before)"
                        label="Válido hasta"
                        prepend-inner-icon="mdi-calendar-end"
                        readonly
                        :messages="expiryHint"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" lg="4">
                      <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                      <v-text-field
                        v-else
                        :model-value="mode2FALabel(data.twofa_mode)"
                        label="Segundo factor"
                        prepend-inner-icon="mdi-two-factor-authentication"
                        readonly
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-col>

              <v-col cols="12">
                <h4 class="text-button text-primary">Vínculos</h4>
                <v-divider />
                <v-container fluid>
                  <v-row>
                    <v-col cols="12" sm="6" lg="4">
                      <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                      <v-text-field
                        v-else
                        :model-value="data.user_id"
                        label="Dueño"
                        prepend-inner-icon="mdi-account"
                        readonly
                        append-inner-icon="mdi-open-in-new"
                        @click:append-inner="router.push(`/admin/users/${data.user_id}/edit`)"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" lg="4">
                      <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                      <v-text-field
                        v-else
                        :model-value="data.key_id"
                        label="Llave"
                        prepend-inner-icon="mdi-key"
                        readonly
                        append-inner-icon="mdi-open-in-new"
                        @click:append-inner="router.push(`/admin/keys/${data.key_id}/edit`)"
                      />
                    </v-col>
                    <v-col v-if="data.csr_id" cols="12" sm="6" lg="4">
                      <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                      <v-text-field
                        v-else
                        :model-value="data.csr_id"
                        label="CSR"
                        prepend-inner-icon="mdi-file-certificate"
                        readonly
                        append-inner-icon="mdi-open-in-new"
                        @click:append-inner="router.push(`/admin/keys/${data.key_id}/csr`)"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-col>

              <v-col cols="12" lg="6">
                <h4 class="text-button text-primary">Estado</h4>
                <v-divider />
                <v-container fluid>
                  <v-skeleton-loader v-if="loading" type="button" />
                  <v-switch
                    v-else
                    v-model="data.state"
                    color="success"
                    inset
                    true-value="ACTIVE"
                    false-value="INACTIVE"
                    :loading="saving"
                    :disabled="saving"
                    :label="data.state === 'ACTIVE' ? 'Activo' : 'Inactivo'"
                    hint="Un certificado inactivo no puede usarse para firmar."
                    persistent-hint
                    @update:model-value="updateState"
                  />
                </v-container>
              </v-col>
            </v-row>
          </v-container>

          <v-card-actions>
            <v-btn
              variant="text"
              color="indigo"
              class="mx-1"
              :disabled="loading || !data.pem"
              @click="downloadPem"
            >
              <v-icon class="mr-1">mdi-download</v-icon>Descargar PEM
            </v-btn>
            <v-spacer />
            <v-btn variant="text" class="mx-1" @click="router.push('/admin/certificates')">
              Volver
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { saveAs } from "file-saver";
import type { CertificateState, Mode2FA } from "bancojs";
import { banco } from "@/api/client";
import { useApiErrors } from "@/composables/useApiErrors";
import { useNotificationsStore } from "@/stores/notifications";
import { mode2FALabel } from "@/utils/labels";

const route = useRoute();
const router = useRouter();
const { report } = useApiErrors();
const notifications = useNotificationsStore();

const fingerprint = computed(() => String(route.params.fingerprint));

const loading = ref(true);
const saving = ref(false);
const data = ref({
  fingerprint: "",
  serialnumber: "",
  state: "ACTIVE" as CertificateState,
  key_id: undefined as number | undefined,
  csr_id: undefined as number | undefined,
  user_id: undefined as number | undefined,
  pem: "",
  valid_after: undefined as string | undefined,
  valid_before: undefined as string | undefined,
  twofa_mode: "NONE" as Mode2FA,
});

function formatDate(value?: string) {
  return value ? new Date(value).toLocaleString("es-CL") : "—";
}

/** Avisa si el certificado ya venció o está por vencer. */
const expiryHint = computed(() => {
  if (!data.value.valid_before) return undefined;
  const days = Math.floor(
    (new Date(data.value.valid_before).getTime() - Date.now()) / 86_400_000
  );
  if (days < 0) return `Venció hace ${Math.abs(days)} día(s).`;
  if (days <= 30) return `Vence en ${days} día(s).`;
  return undefined;
});

async function load() {
  try {
    Object.assign(data.value, await banco.admin.certificates.get(fingerprint.value));
  } catch (err) {
    report(err, {
      title: "Certificado",
      fallback: "Ocurrió un error al recuperar el certificado.",
    });
  } finally {
    loading.value = false;
  }
}

async function updateState(value: CertificateState | null) {
  if (!value) return;
  saving.value = true;
  try {
    await banco.admin.certificates.update(fingerprint.value, { state: value });
    notifications.success(
      value === "ACTIVE" ? "Certificado activado." : "Certificado desactivado.",
      "Guardado"
    );
  } catch (err) {
    // Revertir el switch: si la API rechazó el cambio, la UI no debe quedar
    // mostrando un estado que el servidor no tiene.
    data.value.state = value === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    report(err, {
      title: "Certificado",
      fallback: "Ocurrió un error al cambiar el estado del certificado.",
    });
  } finally {
    // La versión anterior ponía `saving = true` acá dentro del catch, así que
    // tras un error el botón quedaba girando para siempre.
    saving.value = false;
  }
}

function downloadPem() {
  const blob = new Blob([data.value.pem], { type: "application/x-pem-file" });
  saveAs(blob, "certificado.pem");
}

onMounted(load);
</script>
